import { Fragment, useCallback, useEffect, useState } from 'react';
import { getApiErrorMessage } from '../api/client';
import { fetchMember, fetchMembers, mergeMembers, updateMember } from '../api/competitionMember';
import { GRADE_OPTIONS } from '../constants/gradeOptions';
import { useI18n } from '../i18n';
import { translateDivisionLabel, translateRoundLabel } from '../i18n/labels';
import { formatEventDate } from '../utils/competitionList';
import {
  formatAppliedAt,
  formatGender,
  formatGrade,
  formatHeight,
  formatPaymentStatus,
} from '../utils/participantList';
import { formatPhoneInput } from '../utils/registrationValidation';

const PAGE_SIZE = 20;

const EMPTY_EDIT_FORM = {
  name: '',
  phone: '',
  birth: '',
  gender: '',
  parentPhone: '',
  associationName: '',
  gymName: '',
  coachName: '',
  grade: '',
  height: '',
  memo: '',
};

function formatPhone(phone) {
  return phone ? formatPhoneInput(phone) : '-';
}

function formatRank(rank, t) {
  if (rank == null) return '-';
  if (rank === 1) return t('members.records.rank.first');
  if (rank === 2) return t('members.records.rank.second');
  if (rank === 3) return t('members.records.rank.third');
  return t('members.records.rank.nth', { rank });
}

function formatRecord(record, t) {
  if (record.wins == null && record.losses == null) return '-';
  return t('members.records.winLoss', { wins: record.wins ?? 0, losses: record.losses ?? 0 });
}

function formatScoreSum(record, t) {
  if (record.totalScore == null) return '-';
  return t('members.records.score', {
    total: record.totalScore,
    head: record.headScore,
    body: record.bodyScore,
  });
}

// 서버 경기 결과 코드 (WIN / LOSS / BYE / null)
const MATCH_RESULTS = ['WIN', 'LOSS', 'BYE'];

function formatMatchResult(result, t) {
  return MATCH_RESULTS.includes(result)
    ? t(`members.matches.result.${result}`)
    : t('members.matches.result.pending');
}

function formatMatchScore(total, head, body, t) {
  if (total == null && head == null && body == null) return '-';
  return t('members.records.score', { total: total ?? '-', head: head ?? '-', body: body ?? '-' });
}

// 오류 상태는 { key, error } 로 두고 화면에 그릴 때 현재 언어로 만든다
function errorText(state, t) {
  return state ? getApiErrorMessage(state.error, t(state.key)) : null;
}

function toEditForm(member) {
  return {
    name: member.name ?? '',
    phone: formatPhoneInput(member.phone ?? ''),
    birth: member.birth ?? '',
    gender: member.gender ?? '',
    parentPhone: formatPhoneInput(member.parentPhone ?? ''),
    associationName: member.associationName ?? '',
    gymName: member.gymName ?? '',
    coachName: member.coachName ?? '',
    grade: member.grade ?? '',
    height: member.height != null ? String(Number(member.height)) : '',
    memo: member.memo ?? '',
  };
}

function MemberListPage() {
  const { t } = useI18n();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [members, setMembers] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [saving, setSaving] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [expandedRecordId, setExpandedRecordId] = useState(null);

  const loadMembers = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await fetchMembers({ search, page, size: PAGE_SIZE });
      setMembers(data.content ?? []);
      setTotalPages(data.totalPages ?? 0);
      setTotalElements(data.totalElements ?? 0);
    } catch (error) {
      setListError({ key: 'members.loadListError', error });
      setMembers([]);
    } finally {
      setListLoading(false);
    }
  }, [search, page]);

  const loadDetail = useCallback(async (memberId) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      setDetail(await fetchMember(memberId));
    } catch (error) {
      setDetailError({ key: 'members.loadDetailError', error });
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (selectedId == null) {
      setDetail(null);
      return;
    }
    setEditMode(false);
    setMergeTargetId('');
    setExpandedRecordId(null);
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const handleSearch = (event) => {
    event.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  };

  const handleStartEdit = () => {
    if (!detail?.member) return;
    setEditForm(toEditForm(detail.member));
    setEditMode(true);
  };

  const handleEditChange = (field) => (event) => {
    const { value } = event.target;
    setEditForm((prev) => ({
      ...prev,
      [field]: field === 'phone' || field === 'parentPhone' ? formatPhoneInput(value) : value,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!editForm.name.trim() || !editForm.phone.trim()) {
      alert(t('members.requiredFields'));
      return;
    }
    const height = parseFloat(editForm.height);

    setSaving(true);
    try {
      const updated = await updateMember(selectedId, {
        ...editForm,
        birth: editForm.birth || null,
        gender: editForm.gender || null,
        height: Number.isNaN(height) ? null : height,
      });
      setDetail(updated);
      setEditMode(false);
      loadMembers();
    } catch (error) {
      alert(getApiErrorMessage(error, t('members.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleMerge = async () => {
    const targetId = Number(mergeTargetId);
    if (!Number.isInteger(targetId) || targetId <= 0) {
      alert(t('members.merge.targetRequired'));
      return;
    }
    const confirmed = window.confirm(
      t('members.merge.confirm', { name: detail.member.name, id: detail.member.id, targetId })
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const merged = await mergeMembers(detail.member.id, targetId);
      setDetail(merged);
      setSelectedId(merged.member.id);
      loadMembers();
    } catch (error) {
      alert(getApiErrorMessage(error, t('members.merge.error')));
    } finally {
      setSaving(false);
    }
  };

  const member = detail?.member;
  const records = detail?.records ?? [];

  return (
    <div className="container container-wide">
      <div className="page-intro">
        <p className="page-eyebrow">{t('members.eyebrow')}</p>
        <h2 className="page-title">{t('members.title')}</h2>
        <p className="page-desc">{t('members.desc')}</p>
      </div>

      <div className="tournament-layout member-layout">
        <section className="card tournament-select-panel">
          <div className="panel-header">
            <h3 className="panel-title">{t('members.listTitle')}</h3>
            {!listLoading && <span className="participant-count">{t('common.people', { count: totalElements })}</span>}
          </div>

          <form className="member-search-form" onSubmit={handleSearch}>
            <input
              type="search"
              className="form-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('members.searchPlaceholder')}
            />
            <button type="submit" className="btn btn-download">
              {t('common.search')}
            </button>
          </form>

          <div className="tournament-options">
            {listLoading && (
              <div className="list-status">
                <div className="skeleton-card" />
                <div className="skeleton-card" />
              </div>
            )}
            {!listLoading && listError && (
              <div className="list-status list-status-error">
                <p>{errorText(listError, t)}</p>
                <button type="button" className="btn btn-retry" onClick={loadMembers}>
                  {t('common.retry')}
                </button>
              </div>
            )}
            {!listLoading && !listError && members.length === 0 && (
              <p className="panel-empty">{search ? t('members.noSearchResults') : t('members.noMembers')}</p>
            )}
            {!listLoading &&
              !listError &&
              members.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`tournament-card tournament-card-btn${selectedId === m.id ? ' selected' : ''}`}
                  onClick={() => setSelectedId(m.id)}
                >
                  <div className="tournament-card-inner">
                    <div className="tournament-info">
                      <div className="tournament-name">{m.name}</div>
                      <div className="tournament-meta">
                        <span className="meta-chip">{formatPhone(m.phone)}</span>
                        {m.gymName && <span className="meta-chip">{m.gymName}</span>}
                        <span className="meta-chip">{t('members.applicationCount', { count: m.applicationCount })}</span>
                        <span className="meta-chip">{t('members.idChip', { id: m.id })}</span>
                      </div>
                    </div>
                    <div className="tournament-check" aria-hidden="true" />
                  </div>
                </button>
              ))}
          </div>

          {totalPages > 1 && (
            <div className="member-pagination">
              <button
                type="button"
                className="btn btn-prev"
                disabled={page === 0 || listLoading}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('members.prev')}
              </button>
              <span>
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-prev"
                disabled={page + 1 >= totalPages || listLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('members.next')}
              </button>
            </div>
          )}
        </section>

        <section className="card participants-panel">
          {selectedId == null && <p className="panel-empty">{t('members.selectPrompt')}</p>}

          {selectedId != null && detailLoading && (
            <div className="list-status">
              <div className="skeleton-card" />
              <div className="skeleton-card" />
            </div>
          )}

          {selectedId != null && !detailLoading && detailError && (
            <div className="list-status list-status-error">
              <p>{errorText(detailError, t)}</p>
              <button type="button" className="btn btn-retry" onClick={() => loadDetail(selectedId)}>
                {t('common.retry')}
              </button>
            </div>
          )}

          {member && !detailLoading && (
            <>
              <div className="panel-header">
                <div>
                  <h3 className="panel-title">{member.name}</h3>
                  <p className="panel-subtitle">
                    {t('members.subtitle', { id: member.id, date: formatAppliedAt(member.createdAt) })}
                  </p>
                </div>
                {!editMode && (
                  <div className="panel-header-actions">
                    <button type="button" className="btn btn-edit-bracket" onClick={handleStartEdit}>
                      {t('members.edit')}
                    </button>
                  </div>
                )}
              </div>

              {editMode ? (
                <form className="member-edit-form" onSubmit={handleSave}>
                  <div className="member-edit-grid">
                    <label className="form-label">
                      {t('members.required', { label: t('members.fields.name') })}
                      <input className="form-input" value={editForm.name} onChange={handleEditChange('name')} />
                    </label>
                    <label className="form-label">
                      {t('members.required', { label: t('members.fields.phone') })}
                      <input className="form-input" value={editForm.phone} onChange={handleEditChange('phone')} />
                    </label>
                    <label className="form-label">
                      {t('members.fields.birth')}
                      <input
                        type="date"
                        className="form-input"
                        value={editForm.birth}
                        onChange={handleEditChange('birth')}
                      />
                    </label>
                    <label className="form-label">
                      {t('members.fields.gender')}
                      <select className="form-select" value={editForm.gender} onChange={handleEditChange('gender')}>
                        <option value="">{t('members.notSelected')}</option>
                        <option value="M">{t('common.male')}</option>
                        <option value="F">{t('common.female')}</option>
                      </select>
                    </label>
                    <label className="form-label">
                      {t('members.fields.parentPhone')}
                      <input
                        className="form-input"
                        value={editForm.parentPhone}
                        onChange={handleEditChange('parentPhone')}
                      />
                    </label>
                    <label className="form-label">
                      {t('members.fields.association')}
                      <input
                        className="form-input"
                        value={editForm.associationName}
                        onChange={handleEditChange('associationName')}
                      />
                    </label>
                    <label className="form-label">
                      {t('members.fields.gym')}
                      <input className="form-input" value={editForm.gymName} onChange={handleEditChange('gymName')} />
                    </label>
                    <label className="form-label">
                      {t('members.fields.coach')}
                      <input
                        className="form-input"
                        value={editForm.coachName}
                        onChange={handleEditChange('coachName')}
                      />
                    </label>
                    <label className="form-label">
                      {t('members.fields.grade')}
                      <select className="form-select" value={editForm.grade} onChange={handleEditChange('grade')}>
                        {GRADE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.value ? formatGrade(option.value) : t('members.notSelected')}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="form-label">
                      {t('members.fields.heightCm')}
                      <input
                        type="number"
                        step="0.1"
                        className="form-input"
                        value={editForm.height}
                        onChange={handleEditChange('height')}
                      />
                    </label>
                  </div>
                  <label className="form-label">
                    {t('members.fields.memo')}
                    <textarea
                      className="form-input member-memo-input"
                      value={editForm.memo}
                      onChange={handleEditChange('memo')}
                      maxLength={500}
                    />
                  </label>
                  <div className="member-edit-actions">
                    <button type="submit" className="btn btn-save-bracket" disabled={saving}>
                      {saving ? t('members.saving') : t('common.save')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-cancel-edit"
                      onClick={() => setEditMode(false)}
                      disabled={saving}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <dl className="participant-card-meta member-info-grid">
                  <div>
                    <dt>{t('members.fields.phone')}</dt>
                    <dd>{formatPhone(member.phone)}</dd>
                  </div>
                  <div>
                    <dt>{t('members.fields.birth')}</dt>
                    <dd>{member.birth || '-'}</dd>
                  </div>
                  <div>
                    <dt>{t('members.fields.gender')}</dt>
                    <dd>{formatGender(member.gender)}</dd>
                  </div>
                  <div>
                    <dt>{t('members.fields.parentPhone')}</dt>
                    <dd>{formatPhone(member.parentPhone)}</dd>
                  </div>
                  <div>
                    <dt>{t('members.fields.association')}</dt>
                    <dd>{member.associationName || '-'}</dd>
                  </div>
                  <div>
                    <dt>{t('members.fields.gym')}</dt>
                    <dd>{member.gymName || '-'}</dd>
                  </div>
                  <div>
                    <dt>{t('members.fields.coach')}</dt>
                    <dd>{member.coachName || '-'}</dd>
                  </div>
                  <div>
                    <dt>{t('members.fields.grade')}</dt>
                    <dd>{formatGrade(member.grade) || '-'}</dd>
                  </div>
                  <div>
                    <dt>{t('members.fields.height')}</dt>
                    <dd>{formatHeight(member.height)}</dd>
                  </div>
                  {member.memo && (
                    <div className="member-info-memo">
                      <dt>{t('members.fields.memo')}</dt>
                      <dd>{member.memo}</dd>
                    </div>
                  )}
                </dl>
              )}

              <h4 className="member-section-title">{t('members.records.title', { count: records.length })}</h4>
              {records.length === 0 ? (
                <p className="panel-empty">{t('members.records.empty')}</p>
              ) : (
                <div className="member-records-wrap">
                  <table className="member-records-table">
                    <thead>
                      <tr>
                        <th>{t('members.records.columns.competition')}</th>
                        <th>{t('members.records.columns.eventAt')}</th>
                        <th>{t('members.records.columns.gym')}</th>
                        <th>{t('members.records.columns.grade')}</th>
                        <th>{t('members.records.columns.height')}</th>
                        <th>{t('members.records.columns.division')}</th>
                        <th>{t('members.records.columns.rank')}</th>
                        <th>{t('members.records.columns.record')}</th>
                        <th>{t('members.records.columns.scoreSum')}</th>
                        <th>{t('members.records.columns.payment')}</th>
                        <th>{t('members.records.columns.matches')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((record) => (
                        <Fragment key={record.applicationId}>
                          <tr>
                            <td className="cell-name">{record.competitionName}</td>
                            <td>{record.eventAt ? formatEventDate(record.eventAt) : '-'}</td>
                            <td>{record.gymName || '-'}</td>
                            <td>{formatGrade(record.grade) || '-'}</td>
                            <td>{formatHeight(record.height)}</td>
                            <td>{translateDivisionLabel(record.divisionLabel) || '-'}</td>
                            <td>
                              <span className={`rank-badge${record.finalRank === 1 ? ' rank-badge-gold' : ''}`}>
                                {formatRank(record.finalRank, t)}
                              </span>
                            </td>
                            <td>{formatRecord(record, t)}</td>
                            <td>{formatScoreSum(record, t)}</td>
                            <td>{formatPaymentStatus(record.paymentStatus)}</td>
                            <td>
                              {record.matches?.length > 0 ? (
                                <button
                                  type="button"
                                  className="btn-link"
                                  onClick={() =>
                                    setExpandedRecordId((id) =>
                                      id === record.applicationId ? null : record.applicationId
                                    )
                                  }
                                >
                                  {t('members.records.matchCount', { count: record.matches.length })}{' '}
                                  {expandedRecordId === record.applicationId ? '▲' : '▼'}
                                </button>
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                          {expandedRecordId === record.applicationId && (
                            <tr className="member-match-row">
                              <td colSpan={11}>
                                <table className="member-match-table">
                                  <thead>
                                    <tr>
                                      <th>{t('members.matches.columns.round')}</th>
                                      <th>{t('members.matches.columns.opponent')}</th>
                                      <th>{t('members.matches.columns.myScore')}</th>
                                      <th>{t('members.matches.columns.opponentScore')}</th>
                                      <th>{t('members.matches.columns.result')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {record.matches.map((m) => (
                                      <tr key={`${m.roundIndex}-${m.matchIndex}`}>
                                        <td>
                                          {m.divisionLabel ? `${translateDivisionLabel(m.divisionLabel)} ` : ''}
                                          {translateRoundLabel(m.roundLabel)}
                                        </td>
                                        <td>
                                          {m.opponentName
                                            ? `${m.opponentName} (${m.opponentGymName ?? t('common.noGym')})`
                                            : '-'}
                                        </td>
                                        <td>{formatMatchScore(m.totalScore, m.headScore, m.bodyScore, t)}</td>
                                        <td>
                                          {formatMatchScore(
                                            m.opponentTotalScore,
                                            m.opponentHeadScore,
                                            m.opponentBodyScore,
                                            t
                                          )}
                                        </td>
                                        <td>
                                          <span className={`match-result match-result-${(m.result ?? 'none').toLowerCase()}`}>
                                            {formatMatchResult(m.result, t)}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="member-merge">
                <h4 className="member-section-title">{t('members.merge.title')}</h4>
                <p className="panel-subtitle">{t('members.merge.desc')}</p>
                <div className="member-search-form">
                  <input
                    type="number"
                    min="1"
                    className="form-input"
                    value={mergeTargetId}
                    onChange={(e) => setMergeTargetId(e.target.value)}
                    placeholder={t('members.merge.targetPlaceholder')}
                  />
                  <button type="button" className="btn btn-cancel" onClick={handleMerge} disabled={saving}>
                    {t('members.merge.button')}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default MemberListPage;
